<template>
  <div class="sp-number-slider" :class="classModifiers">
    <label v-if="label" class="sp-number-slider__label" part="label" :for="inputId">{{ label }}</label>
    <input
      :id="inputId"
      class="sp-number-slider__input"
      part="input"
      type="number"
      inputmode="numeric"
      :min="minValue"
      :max="maxValue"
      :step="rangeStep"
      :value="currentValue"
      :disabled="disabled"
      :data-testid="inputTestId"
      :aria-label="label"
      @keydown="handleInputKeydown"
      @input.stop="handleInputStep"
      @change.stop="handleInputChange"
    />
    <sp-input-range-slider
      class="sp-number-slider__range"
      part="range"
      :min="rangeMin"
      :max="rangeMax"
      :step="rangeStep"
      :value.prop="rangeValue"
      :show-ticks="hasSnapValues"
      :disabled="disabled"
      :aria-label="label"
      @input="handleSliderInput"
      @change="handleSliderChange"
    ></sp-input-range-slider>
  </div>
</template>

<script setup>
import { computed, ref, toRef, useAttrs, watch } from "vue";
import { props as numberSliderProps } from "./props.js";
import { useCommittedModel } from "#ce/composables/useCommittedModel";
import { useExpose } from "#ce/composables/useExpose";
import { useForm } from "#ce/composables/useForm";
import { useFormControl } from "#ce/composables/useFormControl";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { useSnapValues } from "#ce/composables/useSnapValues";
import { toNumber, trimToUndefined } from "#ce/utils/props";
import { formAssociatedHost } from "#ce/framework/utils";

// sp-number-slider is a genuine form control: submit a value, match :disabled, respond to
// <fieldset disabled>, like the sp-input-range-slider it wraps.
defineOptions(formAssociatedHost());

const props = defineProps(numberSliderProps);

defineEmits(["input", "change"]);
const { emitBubbling } = useHostEmit();

const { disabled } = useFormControl({ disabled: toRef(props, "disabled") });

// Instance-unique id for the label/input association, matching SpRadioGroup's
// pattern: a random suffix avoids cross-instance collisions without module-level
// state, and Vue's useId() can't be used here (each custom element mounts as its
// own app, so every instance would get the same "v-0").
const inputId = `sp-number-slider-input-${Math.random().toString(36).slice(2, 10)}`;

// trimToUndefined so an empty attribute falls back to the default instead of
// Number("") === 0 — a zero step would make the normalize() grid math NaN.
const numericMin = computed(() => toNumber(trimToUndefined(props.min), 1));
const numericMax = computed(() => toNumber(trimToUndefined(props.max), 150));
const numericStep = computed(() => {
  const step = toNumber(trimToUndefined(props.step), 1);
  return step > 0 ? step : 1;
});

const { snapValues, hasSnapValues, nearestSnapIndex, nearestSnapValue, adjacentSnapValue } = useSnapValues(
  toRef(props, "values"),
);

const maxValue = computed(() => (hasSnapValues.value ? snapValues.value.at(-1) : numericMax.value));
const minValue = computed(() => (hasSnapValues.value ? snapValues.value[0] : numericMin.value));

// `input` fires on every adjustment (each slider drag tick); `change` fires
// only on commit — thumb release or a numeric-input edit — so consumers that
// fetch per change (the finder) aren't hammered while the thumb is dragged.
// The `value` attribute seeds the slider and names the value a reset restores; the `value`
// property is the live position. A declared prop would install a non-configurable host accessor
// that returns the seed forever.
const attrs = useAttrs();
const defaultValue = ref(attrs.value);
const { model, update, commit, set } = useCommittedModel(defaultValue, {
  onUpdate: (value) => emitBubbling("input", value),
  onCommit: (value) => emitBubbling("change", value),
});

watch(
  () => attrs.value,
  (value) => (defaultValue.value = value),
);

// An unset model reads as the largest selectable value, and that one value is what the live
// `value` property reports, the inner controls render, and the form submits.
const currentValue = computed(() => model.value ?? maxValue.value);

const { exposeProperties } = useExpose();

exposeProperties({
  value: {
    configurable: true,
    get: () => currentValue.value,
    set: (value) => set(toNumber(trimToUndefined(value))),
  },
  defaultValue: {
    configurable: true,
    get: () => defaultValue.value,
  },
});

// The inner slider sits in the shadow root, where the outer form cannot see it, so the element
// submits through ElementInternals. `change` is already reported on commit above.
const { mirrorFormValue, onFormReset } = useForm();
const formValue = computed(() => String(currentValue.value ?? ""));

mirrorFormValue(formValue);

onFormReset(() => set(toNumber(trimToUndefined(defaultValue.value))));

// With snap values the inner slider works on the list index — one tick per
// entry — so uneven gaps (…25, 50, 100…) each take one step of travel.
const rangeMin = computed(() => (hasSnapValues.value ? 0 : numericMin.value));
const rangeMax = computed(() => (hasSnapValues.value ? snapValues.value.length - 1 : numericMax.value));
const rangeStep = computed(() => (hasSnapValues.value ? 1 : numericStep.value));
const rangeValue = computed(() => (hasSnapValues.value ? nearestSnapIndex(currentValue.value) : currentValue.value));

const classModifiers = computed(() => {
  const modifiers = {};

  if (disabled.value) {
    modifiers["--disabled"] = true;
  }

  return modifiers;
});

function normalize(value) {
  if (hasSnapValues.value) {
    return nearestSnapValue(value);
  }

  // Align to the step grid (anchored at min, like the native stepper), then clamp.
  const stepped = numericMin.value + Math.round((value - numericMin.value) / numericStep.value) * numericStep.value;
  return Math.min(numericMax.value, Math.max(numericMin.value, stepped));
}

function handleInputChange(event) {
  const value = toNumber(trimToUndefined(event.target.value));

  if (value === undefined) {
    // Restore the previous value when the field is cleared or non-numeric.
    event.target.value = currentValue.value;
    return;
  }

  const normalized = normalize(value);
  event.target.value = normalized;
  commit(normalized);
}

// With snap values, stepping must jump to the adjacent list entry (15 → 20),
// not by the input's uniform step attribute — a native number input can't
// express uneven steps. Arrow keys are intercepted here; spinner clicks are
// caught in handleInputStep below.
function handleInputKeydown(event) {
  if (hasSnapValues.value && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
    event.preventDefault();
    stepToAdjacent(event.key === "ArrowUp" ? 1 : -1, event.target);
  }
}

// A spinner-button click moves the value by one step and fires `input` as a
// plain Event with no `inputType` in Chrome and Safari, but as an InputEvent
// with inputType "insertReplacementText" in Firefox (all verified in-browser).
// (Arrow-key steps fire the same event but never reach here: handleInputKeydown
// intercepts them.) Typing (`insertText`) and pasting (`insertFromPaste`) carry
// their own inputType; neither may be misread as a spinner step (e.g. pasting
// 26 over 25), so they bail here and are left for the change commit to snap.
// "insertReplacementText" is also autofill's inputType, which the ±1 delta
// check below screens out: a whole-value replacement rarely lands exactly one
// native step away, and one that does just steps to the adjacent snap stop.
function handleInputStep(event) {
  if (!hasSnapValues.value || (event.inputType && event.inputType !== "insertReplacementText")) {
    return;
  }

  const diff = toNumber(trimToUndefined(event.target.value)) - currentValue.value;

  if (Math.abs(diff) === 1) {
    stepToAdjacent(diff, event.target);
  }
}

function stepToAdjacent(direction, input) {
  const next = adjacentSnapValue(currentValue.value, direction);

  if (next === undefined) {
    input.value = currentValue.value;
    return;
  }

  input.value = next;
  commit(next);
}

function handleSliderInput({ detail }) {
  const [value] = detail;
  update(hasSnapValues.value ? snapValues.value[value] : value);
}

function handleSliderChange({ detail }) {
  const [value] = detail;
  commit(hasSnapValues.value ? snapValues.value[value] : value);
}
</script>

<style>
:host {
  display: block;

  --label-font: var(--sp-comp-number-slider-label-font, var(--sp-sys-typescale-small-short));
  --label-color: var(--sp-comp-number-slider-label-color, var(--sp-sys-color-on-surface));
  --input-font: var(--sp-comp-number-slider-input-font, var(--sp-sys-typescale-body-short));
  --input-color: var(--sp-comp-number-slider-input-color, var(--sp-sys-color-on-surface));
  --input-bg-color: var(--sp-comp-number-slider-input-bg-color, var(--sp-sys-color-surface, #fff));
  --input-border-color: var(--sp-comp-number-slider-input-border-color, var(--sp-sys-color-outline, #ccc));
  --input-border-radius: var(--sp-comp-number-slider-input-border-radius, 0.25rem);
  --input-padding: var(--sp-comp-number-slider-input-padding, var(--sp-ref-spacing-3) var(--sp-ref-spacing-4));
  --gap: var(--sp-comp-number-slider-gap, var(--sp-ref-spacing-4));
  --slider-gap: var(--sp-comp-number-slider-slider-gap, var(--sp-ref-spacing-2));
}

:host(:disabled) {
  cursor: not-allowed;
  opacity: 0.6;
}
</style>

<style scoped lang="scss">
.sp-number-slider {
  display: flex;
  flex-direction: column;
  gap: var(--gap);

  &__label {
    font: var(--label-font);
    color: var(--label-color);
  }

  &__input {
    font: var(--input-font);
    color: var(--input-color);
    background-color: var(--input-bg-color);
    border: 1px solid var(--input-border-color);
    border-radius: var(--input-border-radius);
    padding: var(--input-padding);
    width: 100%;
    box-sizing: border-box;
  }

  // On top of the flex gap, so the slider sits further from the input than the
  // input does from the label.
  &__range {
    margin-top: var(--slider-gap);
  }

  &.--disabled &__input {
    pointer-events: none;
  }
}
</style>
